import { ForceGraph3DInstance } from "3d-force-graph";
import { Box3, BoxGeometry, Mesh, Object3D, PerspectiveCamera, Vector3 } from "three";
import { NodePositionMap } from "./node-layouts";
import { headNodeParameters } from "./3d-rendering-parameters";

const nodeExpansionScalar = 2.6;
const nodeHeight = headNodeParameters.headSize * nodeExpansionScalar;
const nodeWidth = headNodeParameters.headSize * nodeExpansionScalar;
const nodeDepth = headNodeParameters.headSize * nodeExpansionScalar;

const size = new Vector3();
const center = new Vector3();
const box = new Box3();

function getCameraPositionForSelection(
  graph: ForceGraph3DInstance,
  selection: Object3D[],
  resetAlignment: boolean,
  fitOffset = 1.2,
): { x: number; y: number; z: number } {
  const camera = graph.camera() as PerspectiveCamera;

  box.makeEmpty();
  selection.forEach((obj) => {
    box.expandByObject(obj, true);
  });

  box.getSize(size);
  box.getCenter(center);

  const maxSize = Math.max(size.x, size.y, size.z);
  const fitHeightDistance = maxSize / (2 * Math.atan((Math.PI * camera.fov) / 360));
  const fitWidthDistance = fitHeightDistance / camera.aspect;
  const distance = fitOffset * Math.max(fitHeightDistance, fitWidthDistance);
  if (resetAlignment) {
    return {
      x: 0,
      y: 0,
      z: distance,
    };
  }

  const { x: camX, y: camY, z: camZ } = graph.cameraPosition();
  const camMultiplier = distance / Math.sqrt(camX ** 2 + camY ** 2 + camZ ** 2);
  return { x: camMultiplier * camX, y: camMultiplier * camY, z: camZ * camMultiplier };
}

function boxObjectFromNode(position: { x: number; y: number; z: number }) {
  const nodeBox = new BoxGeometry(nodeWidth, nodeHeight, nodeDepth);
  const nodeObject = new Mesh(nodeBox);
  nodeObject.position.set(position.x, position.y, position.z);
  return nodeObject;
}

export function positionCameraForSingleNode(graph: ForceGraph3DInstance, transitionMs = 0): void {
  const newCameraPosition = getCameraPositionForSelection(
    graph,
    [boxObjectFromNode({ x: 0, y: 0, z: 0 })],
    true,
    1,
  );
  graph.cameraPosition(newCameraPosition, { x: 0, y: 0, z: 0 }, transitionMs);
}
export function positionCameraForNodes(
  graph: ForceGraph3DInstance,
  nodePositions: NodePositionMap,
  transitionMs = 0,
): void {
  const nodeObjectsFromPositions = Object.values(nodePositions).map((position) => {
    return boxObjectFromNode(position);
  });
  const newCameraPosition = getCameraPositionForSelection(
    graph,
    nodeObjectsFromPositions,
    true,
    1.3, // For multiple nodes, leave more extra space for labels and rotation
  );
  graph.cameraPosition(newCameraPosition, { x: 0, y: 0, z: 0 }, transitionMs);
}

export function zoomCameraToDistanceForSingleNode(
  graph: ForceGraph3DInstance,
  transitionMs = 0,
): void {
  const newCameraPosition = getCameraPositionForSelection(
    graph,
    [boxObjectFromNode({ x: 0, y: 0, z: 0 })],
    false,
    1,
  );
  graph.cameraPosition(newCameraPosition, { x: 0, y: 0, z: 0 }, transitionMs);
}
